import React, { useRef, useState, useEffect } from "react";
import { LogIn, LogOut, ShieldCheck, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { startLogin } from "@/lib/auth";
import { getUsage } from "@/lib/api";

/**
 * Header user menu: shows either a "Sign in" button or the user's avatar
 * with a small dropdown (email + logout). No routing dependencies.
 */
export default function UserMenu() {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const wrapperRef = useRef(null);

  // Fetch remaining credits so we can show the badge above the username.
  // Refresh whenever the dropdown opens and on a light interval (60s) so
  // the header stays in sync after an analyze/mastering job completes.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchUsage = () => {
      getUsage()
        .then((u) => { if (!cancelled) setUsage(u); })
        .catch(() => {});
    };
    fetchUsage();
    const iv = setInterval(fetchUsage, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (loading) {
    return (
      <div
        className="w-8 h-8 rounded-full bg-white/5 animate-pulse"
        data-testid="user-menu-loading"
      />
    );
  }

  if (!user) {
    const handleLogin = async () => {
      try {
        await startLogin();
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("auth.sign_in_error"));
      }
    };
    return (
      <button
        onClick={handleLogin}
        className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
        data-testid="header-login-btn"
      >
        <LogIn size={14} />
        <span className="hidden sm:inline">{t("auth.sign_in")}</span>
      </button>
    );
  }

  const initials =
    (user.name || user.email || "?")
      .split(/\s+/)
      .map((s) => s[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 border border-white/10 hover:border-white/20 transition-colors"
        data-testid="header-user-menu-btn"
        aria-label={t("auth.account")}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            className="w-7 h-7 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white text-[11px] font-bold flex items-center justify-center">
            {initials}
          </div>
        )}
        <div className="hidden md:flex flex-col items-start leading-tight max-w-[160px]">
          {usage && !user.is_admin && typeof usage.remaining === "number" && (
            <span
              className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--brand)] tabular-nums"
              data-testid="header-credits-badge"
            >
              <Coins size={10} />
              {t("auth.credits_remaining", { n: usage.remaining })}
            </span>
          )}
          <span className="text-xs text-white truncate max-w-[160px]">
            {user.name || user.email}
          </span>
        </div>
        {user.is_admin && (
          <ShieldCheck size={12} className="text-[color:var(--meter-excellent)]" aria-label="admin" />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[240px] rounded-xl border border-white/10 bg-[color:var(--bg-elev)] shadow-xl overflow-hidden z-40"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-white/5">
            <div className="text-xs text-[color:var(--fg-muted)] tiny-caps">
              {user.is_admin ? t("auth.admin_label") : t("auth.account")}
            </div>
            <div className="text-sm text-white font-medium truncate mt-0.5">
              {user.name || user.email}
            </div>
            {user.name && (
              <div className="text-[11px] text-[color:var(--fg-secondary)] truncate">
                {user.email}
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors flex items-center gap-2"
            data-testid="header-logout-btn"
          >
            <LogOut size={14} />
            {t("auth.sign_out")}
          </button>
        </div>
      )}
    </div>
  );
}
